import { useState, useRef, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import beforeMask from '@/assets/before-mask.jpg';
import afterMask from '@/assets/after-mask.jpg';

const BeforeAfter = () => {
  const { t } = useLanguage();
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  }, []);

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e: React.MouseEvent) => { if (isDragging.current) updatePosition(e.clientX); };
  const handleTouchMove = (e: React.TouchEvent) => { updatePosition(e.touches[0].clientX); };

  return (
    <section className="py-20 md:py-32 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-5xl md:text-7xl tracking-wider text-foreground" style={{ fontFamily: 'Bebas Neue' }}>
            {t.beforeAfter.title}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">{t.beforeAfter.subtitle}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          ref={containerRef}
          className="relative aspect-square max-w-2xl mx-auto overflow-hidden rounded-sm cursor-col-resize select-none"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onMouseMove={handleMouseMove}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
        >
          {/* After (full) */}
          <img src={afterMask} alt="After" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '50% 40%', transform: 'scale(1.15)' }} />

          {/* Before (clipped) */}
          <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
            <img src={beforeMask} alt="Before" className="w-full h-full object-cover" style={{ objectPosition: '50% 40%', transform: 'scale(1.15)' }} />
          </div>

          {/* Slider line */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-primary z-10"
            style={{ left: `${sliderPos}%` }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg">
              <div className="flex gap-0.5">
                <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-primary-foreground" />
                <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-primary-foreground" />
              </div>
            </div>
          </div>

          {/* Labels */}
          <div className="absolute top-4 left-4 bg-background/80 px-3 py-1 text-xs uppercase tracking-widest text-foreground rounded-sm">
            {t.beforeAfter.before}
          </div>
          <div className="absolute top-4 right-4 bg-background/80 px-3 py-1 text-xs uppercase tracking-widest text-foreground rounded-sm">
            {t.beforeAfter.after}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BeforeAfter;
